import React, { Dispatch, SetStateAction, useState } from 'react';
import { colors } from 'utils/constants';
import PrizeTierBadge from '../PrizeTierBadge';
import styles from './PrizeBanner.module.css';
import QuestionMarkInCircle19 from '../../svg/QuestionMarkInCircle19.svg';
import { getPrizeTierByKey, IPrizeTier } from 'constants/LottoPrizeValues';
import GenericModal from 'components/GenericModal';
import RoundedButton from 'components/RoundedButton';

interface Props {
  prizeTier: string|undefined;
  premiumCriteria: string|undefined;
  liteCriteria: string|undefined;
  freemiumCriteria: string|undefined;
  boostRequired: boolean;
}
export const PrizeBanner = (props: Props) => {
  const [showInfo, setShowInfo] = useState(false);  

  const prizeTier = getPrizeTierByKey(props.prizeTier || '');
  if (!prizeTier) {
    return null;
  }
  return (
    <div className={styles.container}>
      <button
        className={styles.tierInfoButton}
          onClick={() => setShowInfo(true)}
          aria-label="Boost your entries to unlock prize"
        >
          <QuestionMarkInCircle19 />
      </button>
      <div className={styles.prizeBadge} style={{color: prizeTier.color}}>
        <img className={styles.badge} src={prizeTier.badge} alt={'Badge for ' + prizeTier.title + ' ' + (prizeTier.subtitle || '')} />
        {prizeTier.title}
        <em>{prizeTier.subtitle}</em>
      </div>
      <div className={styles.prizeCriteria}>
        This is a {prizeTier.title}{prizeTier.subtitle && <em>{prizeTier.subtitle}</em>} prize, match{' '}
        <strong>{getFormattedMatchString(props.premiumCriteria, true)}</strong>{' '}
        {props.boostRequired ? 'with boosted entries to win' : 'to win (Premium plan)'}
      </div>
      {
        showInfo &&
        <GenericModal onClose={() => setShowInfo(false)} style={{maxWidth: 840, width: 'auto'}}>
            <img className={styles.popupBadge} src={prizeTier.badge} alt="" />
            <h2>
              <div className="aboutUsTypographyHeaderLarge">{prizeTier.title}{prizeTier.subtitle && <em>{prizeTier.subtitle}</em>} prizes</div>
              <div className={styles.headingSubtext}>worth up to £{prizeTier.value.toLocaleString('en-GB')}</div>
            </h2>
            <table className={styles.table}>
              <tr className="aboutUsTypographyHeaderMedium">
                <th><span className={styles.desktopOnly}>Weekly{'\u00A0'}</span>Plan</th>
                <th>Numbers{' '}<span className={styles.desktopOnly}>needed{' '}</span>to win</th>
              </tr>
              {getTableRowsForMatches(prizeTier, props).map((columns, index) =>
                <tr key={index}>
                  <td>{columns[0]}</td>
                  <td>{columns[1]}</td>
                </tr>
              )}
            </table>
            <p className={styles.popupButtonContainer}>
              <RoundedButton rebrandDm2025 onClick={() =>setShowInfo(false)}>Got it</RoundedButton>
              </p>
        </GenericModal>
      }
    </div>
  );
};

/** Expects shorthand string (e.g. "4+1") and outputs longhand user-facing string (E.g. "Match 4 + BB") */
function getFormattedMatchString(matchString: string|undefined, brief: boolean) {
  if (!matchString) {
    return undefined
  }
  const [mainNumberMatches, bonusMatches] = matchString.split('+');
  let valueString = mainNumberMatches + ' main';
  if (!brief) {
    valueString += ' numbers';
  }
  if (bonusMatches === '1') {
    valueString += ' + the bonus ball';
  }
  return valueString;
};

function getTableRowsForMatches(prizeTier: IPrizeTier, props: Props) {
  if (props.boostRequired) {
    return [
      [
        'Premium + Lite', '4 main + the bonus with boosted entries'
      ]
    ];
  }

  return [
    ['Premium', getFormattedMatchString(props.premiumCriteria, false)],
    ['Lite', getFormattedMatchString(props.liteCriteria, false) || 'Not available for Lite members'],
  ]
}